/**
 * Step-up from the web (XC-01, FR-E55) — a fresh two-step verification in front of an action
 * that cannot be undone, obtained from core-mfa through auth and passed to ProGST as
 * `X-StepUp-Assertion` (`api()`'s `stepUpAssertion`).
 *
 * **What the platform serves, cited, not invented** — `repo:fiveox:services/auth-service/app/api/stepup.py`
 * (core-mfa P3, `repo:fiveox:docs/sessions/2026-08-04-core-mfa-p3-stepup.md`;
 * APP_DEVELOPMENT_GUIDE §7.2):
 *
 * - `POST {AUTH_API}/stepup/otp/send` `{method: 'email' | 'sms'}` — sends a code to the person's
 *   verified channel; refused when that channel is not verified.
 * - `POST {AUTH_API}/stepup/challenge` `{client_id, method, code}` with the person's bearer —
 *   `method` is `totp | backup_code | email | sms`; answers
 *   `{data: {stepup_assertion, expires_in: 300}}`: an RS256 token with `purpose=stepup`,
 *   `aud` = the requesting app's `client_id`, `amr` = the factor. Refused with
 *   "Two-step verification is not enabled for this account." when no factor is enrolled.
 *
 * What ProGST's service checks (`services/gst-service/app/platform/stepup.py`): the header is
 * present, the token verifies against the issuer's JWKS, `aud` is ProGST's `client_id` (the
 * same `SSO_CLIENT_ID` sent here), and `sub` is the signed-in person. So the assertion is
 * asked for with `SSO_CLIENT_ID` and nothing else.
 *
 * **Held for its life, in memory only.** The service does not spend an assertion, so one
 * verification covers the next few irreversible steps of the same sitting — submit, proceed
 * and file within five minutes — less a 30-second margin. Never written to storage: a reload
 * asks again.
 */

import { AUTH_API, SSO_CLIENT_ID } from './config'
import { session } from './session'

export type StepUpMethod = 'totp' | 'backup_code' | 'email' | 'sms'

export interface StepUpChoice {
  value: StepUpMethod
  label: string
  /** Whether a code has to be sent first (email and SMS); an authenticator app needs nothing. */
  sends: boolean
}

export const STEP_UP_CHOICES: StepUpChoice[] = [
  { value: 'totp', label: 'Authenticator app', sends: false },
  { value: 'email', label: 'Code by email', sends: true },
  { value: 'sms', label: 'Code by SMS', sends: true },
  { value: 'backup_code', label: 'Backup code', sends: false },
]

/** Seconds of an assertion's life not used, so it never expires between click and request. */
export const MARGIN_SECONDS = 30

let held: { assertion: string; until: number } | null = null

/** The assertion still in hand, or null when there is none or it is about to expire. */
export function heldAssertion(now: number = Date.now()): string | null {
  return held && now < held.until ? held.assertion : null
}

/** Forget it — on a refusal the service called invalid, and in tests. */
export function forgetAssertion(): void {
  held = null
}

export class StepUpError extends Error {}

async function post(path: string, body: unknown): Promise<Record<string, unknown>> {
  let res: Response
  try {
    res = await session.authed(`${AUTH_API}${path}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    })
  } catch {
    throw new StepUpError('The sign-in service could not be reached to verify you.')
  }
  const parsed = (await res.json().catch(() => null)) as
    | { data?: Record<string, unknown>; error?: { message?: string } }
    | null
  if (!res.ok) {
    throw new StepUpError(parsed?.error?.message ?? 'The verification was refused.')
  }
  return parsed?.data ?? {}
}

/** Email or SMS: ask auth to send the code. */
export async function sendStepUpCode(method: 'email' | 'sms'): Promise<void> {
  await post('/stepup/otp/send', { method })
}

/** Trade the code for an assertion, and hold it for its life less the margin. */
export async function obtainStepUp(
  method: StepUpMethod,
  code: string,
  now: () => number = Date.now,
): Promise<string> {
  const data = await post('/stepup/challenge', { client_id: SSO_CLIENT_ID, method, code })
  const assertion = typeof data.stepup_assertion === 'string' ? data.stepup_assertion : ''
  if (!assertion) throw new StepUpError('The verification answered without an assertion.')
  const life = typeof data.expires_in === 'number' ? data.expires_in : 0
  held = { assertion, until: now() + Math.max(0, life - MARGIN_SECONDS) * 1000 }
  return assertion
}
